import React from "react"

import { Tooltip } from "@coval/ui/tooltip"

import {
  ATTRIBUTE_TEMPLATE_PATTERN,
  resolveAttributeToken,
  type AttributeContext,
} from "./replace_with_attributes"

/**
 * Render a metric question with its `{{prefix.key}}` template variables resolved
 * from the call's attribute context. Substituted values get a subtle highlight
 * and, on hover, a tooltip showing the original `{{...}}` token they came from.
 * Tokens that don't resolve are left as their literal `{{...}}` text.
 *
 * Display-only: resolves a single pass (no nested dynamic keys), which is enough
 * for the current single-level attributes.
 */
export const renderQuestionWithAttributes = (
  question: string,
  context: AttributeContext,
): React.ReactNode => {
  if (!question) {
    return question
  }

  const regex = new RegExp(ATTRIBUTE_TEMPLATE_PATTERN.source, "g")
  const parts: React.ReactNode[] = []
  let lastIndex = 0
  let key = 0
  let match: RegExpExecArray | null

  while ((match = regex.exec(question)) !== null) {
    if (match.index > lastIndex) {
      parts.push(question.slice(lastIndex, match.index))
    }

    const token = match[0]
    const value = resolveAttributeToken(match[1], match[2], context)
    if (value !== null) {
      parts.push(
        <Tooltip
          key={`sub-${key}`}
          content={
            <div className="px-1 py-0.5">
              <div className="text-xs font-mono text-coval-text-muted">
                {token}
              </div>
            </div>
          }
        >
          <span className="rounded-[3px] bg-coval-info-surface px-0.5 text-coval-info-on-surface">
            {value}
          </span>
        </Tooltip>,
      )
      key += 1
    } else {
      parts.push(token)
    }

    lastIndex = regex.lastIndex
  }

  if (lastIndex < question.length) {
    parts.push(question.slice(lastIndex))
  }

  return parts.length > 0 ? <>{parts}</> : question
}
